import { DesignSystemVersion, DsFile, ChatRef } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import {
	updateActiveDesignSystemVersion,
	updateDsChatRefs,
	updateLatestPublishedVersionNumber,
	setDesignSystemLoadError,
} from '../redux/db/actions';
import {
	setDesignSystemContent,
	showMigrationModal,
	resolveOptimisticDsFileWrites,
} from '../redux/mode/actions';
import { displayMigrationRequiredModal } from '../redux/mode/util';
import { createDesignSystemContentFromFiles } from '../designSystem/content/factory';
import { applyDsFileWrites, findResolvedDsFileWrites } from '../redux/mode/util/dsFiles';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class DesignSystemVersionPollingStream extends PollingStream<DesignSystemVersion | null> {
	private lastFiles: DsFile[] | null = null;
	private lastIsLegacy: boolean = false;
	private lastChatRefs: ChatRef[] = [];
	private lastLatestPublishedVersionNumber: number | null = null;

	constructor(designSystemId: string) {
		const url = VITE_FORGE_URL + `/poll/design-system-version/${designSystemId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setDesignSystemLoadError('You do not have access to this design system'));
				store.dispatch(updateActiveDesignSystemVersion(null));
				store.dispatch(updateLatestPublishedVersionNumber(null));
				store.dispatch(setDesignSystemContent(null));
				store.dispatch(updateDsChatRefs([]));
			} else {
				console.error('DesignSystemVersionPollingStream error:', error);
			}
		};

		super(url, onError, 'activeDesignSystemVersion');
	}

	extractData(response: any): DesignSystemVersion[] {
		// DesignSystemVersion endpoint returns a single DesignSystemVersion object or null
		if (response?.version === null || response?.version === undefined) {
			this.lastFiles = null;
			this.lastIsLegacy = false;
			this.lastChatRefs = [];
			this.lastLatestPublishedVersionNumber = null;
			return [];
		}

		this.lastFiles = response.files || null;
		this.lastIsLegacy = response.isLegacy || false;
		this.lastChatRefs = response.chatRefs || [];
		this.lastLatestPublishedVersionNumber = response.latestPublishedVersionNumber ?? null;

		return [response.version];
	}

	onData(versions: DesignSystemVersion[]) {
		const version = versions.length > 0 ? versions[0] : null;
		if (!version) {
			store.dispatch(updateActiveDesignSystemVersion(null));
			store.dispatch(updateLatestPublishedVersionNumber(null));
			store.dispatch(setDesignSystemContent(null));
			store.dispatch(updateDsChatRefs([]));
			return;
		}

		// The backend already returns DesignSystemVersion objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedVersion: DesignSystemVersion = {
			...version,
			createdAt: new Date(version.createdAt),
		};

		// since DS versions don't have optimistic writes (they aren't changed on the FE currently) we don't need to resolve them
		store.dispatch(updateActiveDesignSystemVersion(processedVersion));

		// Process chat refs - convert date strings to Date objects
		const processedChatRefs: ChatRef[] = this.lastChatRefs.map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateDsChatRefs(processedChatRefs));

		// Update latest published version number
		store.dispatch(updateLatestPublishedVersionNumber(this.lastLatestPublishedVersionNumber));

		// Handle legacy DS - trigger migration modal (only if not already showing)
		if (this.lastIsLegacy) {
			const state = store.getState();
			if (state.mode.migrationModal?.dsId !== processedVersion.designSystemId) {
				displayMigrationRequiredModal(processedVersion.designSystemId, 'editor');
			}
			// Don't set content until migrated (only dispatch if not already null)
			if (state.mode.designSystemContent !== null) {
				store.dispatch(setDesignSystemContent(null));
			}
			return;
		}

		// Migration completed - close modal if it was open for this DS
		const state = store.getState();
		if (state.mode.migrationModal?.dsId === processedVersion.designSystemId) {
			store.dispatch(showMigrationModal(null));
		}

		// Process files, applying optimistic writes and resolving outcome-matched writes.
		const serverFiles = this.lastFiles || [];
		const writes = state.mode.optimisticDsFileWrites;

		if (serverFiles.length > 0 || writes.length > 0) {
			const resolvedIds = findResolvedDsFileWrites(writes, serverFiles);
			if (resolvedIds.length > 0) {
				store.dispatch(resolveOptimisticDsFileWrites(resolvedIds));
			}

			const remainingWrites = writes.filter((w) => !resolvedIds.includes(w.id));
			const patchedFiles = applyDsFileWrites(serverFiles, remainingWrites);

			const content = createDesignSystemContentFromFiles(processedVersion.id, patchedFiles);
			store.dispatch(setDesignSystemContent(content));
		} else {
			// If no files, set content to null and clear optimistic writes
			store.dispatch(setDesignSystemContent(null));
		}
	}
}
